if (!customElements.get('new-configurator')) {
  class NewConfigurator extends HTMLElement {
    constructor() {
      super();
      this.options = this.querySelectorAll('[data-option]');
      this.configuratorSidebar = document.querySelector('configurator-sidebar');
      this.configuratorSlider = this.configuratorSidebar.querySelector('configurator-slider');
      this.prices = document.querySelectorAll('[data-product-info] [data-final-price]');
      this.comparePrices = document.querySelectorAll('[data-product-info] [data-compare-price]');

      this.addButton = document.querySelector('[data-product-form-button]');

      this.bowlTitles = this.querySelectorAll('[data-bowl-title]');
      this.bowlButtons = this.querySelectorAll('[data-bowl-button]');
      this.selectedBowlType = this.getSelectedBowlTypeFromDom();
      this.selectedBowlSku = this.getSelectedBowlSkuFromDom();
      this.onBowlTitleClick = this.handleBowlTitleClick.bind(this);
      this.onBowlButtonClick = this.handleBowlButtonClick.bind(this);

      this.setupOptionClickListeners();
      this.setupAddButtonListener();
      this.setupBowlPickerListeners();
    }

    setupBowlPickerListeners() {
      if (!this.bowlTitles?.length || !this.bowlButtons?.length) return;

      this.bowlTitles.forEach((el) => el.addEventListener('click', this.onBowlTitleClick));
      this.bowlButtons.forEach((el) => {
        el.addEventListener('click', this.onBowlButtonClick);
        // role="button" needs Enter/Space wired up by hand.
        el.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          el.click();
        });
      });
    }

    handleBowlTitleClick(event) {
      const el = event?.currentTarget instanceof Element ? event.currentTarget : null;
      if (!el) return;
      const index = Array.from(this.bowlTitles).indexOf(el);
      this.setSelectedBowlByIndex(index);
    }

    handleBowlButtonClick(event) {
      const el = event?.currentTarget instanceof Element ? event.currentTarget : null;
      if (!el) return;
      const index = Array.from(this.bowlButtons).indexOf(el);
      this.setSelectedBowlByIndex(index);
    }

    getSelectedBowlTypeFromDom() {
      const selected =
        Array.from(this.bowlTitles || []).find((el) => el.hasAttribute('data-selected')) ||
        (this.bowlTitles?.[0] || null);
      if (!selected) return '';
      const textNode = selected.querySelector('[data-bowl-text]');
      return ((textNode || selected).textContent || '').trim();
    }

    getSelectedBowlSkuFromDom() {
      const selected =
        Array.from(this.bowlButtons || []).find((el) => el.hasAttribute('data-selected')) ||
        (this.bowlButtons?.[0] || null);
      if (!selected) return '';
      return (selected.dataset.bowlSku || '').trim();
    }

    getSelectedBowlType() {
      return (this.selectedBowlType || '').trim();
    }

    getSelectedBowlSku() {
      return (this.selectedBowlSku || '').trim();
    }

    setSelectedBowlByIndex(index) {
      const safeIndex = Number.isInteger(index) ? index : -1;
      if (safeIndex < 0) return;

      const title = this.bowlTitles?.[safeIndex] || null;
      const type = title ? (title.textContent || '').trim() : '';
      if (!type) return;

      this.bowlTitles?.forEach((el, i) => {
        if (i === safeIndex) el.setAttribute('data-selected', '');
        else el.removeAttribute('data-selected');
      });

      this.bowlButtons?.forEach((el, i) => {
        if (i === safeIndex) el.setAttribute('data-selected', '');
        else el.removeAttribute('data-selected');
        el.setAttribute('aria-pressed', i === safeIndex ? 'true' : 'false');
      });

      this.selectedBowlType = type;
      const selectedButton = this.bowlButtons?.[safeIndex] || null;
      this.selectedBowlSku = selectedButton ? (selectedButton.dataset.bowlSku || '').trim() : '';
    }

    setupOptionClickListeners() {
      this.options.forEach(option => {
        option.addEventListener('click', (event) => this.handleOptionClick(option, event));

        // The slot is a role="button" — Enter/Space have to be wired up by hand.
        // Clicking an empty slot opens the picker sidebar, so this is the only
        // way into the configurator by keyboard. Keys aimed at the nested remove
        // button are left to its own handler.
        option.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          if (event.target !== option) return;
          event.preventDefault();
          option.click();
        });
      });
    }

    handleOptionClick(option, event) {
      event.stopPropagation();

      // Check if the click is on the remove button
      const removeButton = event.target.closest('[data-remove]');
      if (removeButton) {
        // If clicked on remove button, remove the option
        if (option.hasAttribute('data-checked')) {
          this.handleOptionUnselect(option);
        }
        return;
      }

      // If not clicked on remove button, open sidebar
      this.configuratorSidebar?.openSidebar();
    }

    handleOptionUnselect(option) {
      option.removeAttribute('data-checked');
      this.resetOptionImage(option);

      const optionIndex = Array.from(this.options).indexOf(option);
      if (this.configuratorSidebar) {
        this.removeProductFromSidebar(optionIndex);
      }
    }

    resetOptionImage(option) {
      this.setOptionImage(option.querySelector('img'), this.configuratorSidebar?.state.notSelectedImage);
    }

    removeProductFromSidebar(optionIndex) {
      const product = this.configuratorSidebar?.state?.optionsRender?.[optionIndex];
      this.configuratorSidebar.removeProductFromState(optionIndex);
      if (product) {
        this.configuratorSidebar.syncProductCardAfterRemoval(product.id);
      }
      this.configuratorSidebar.renderProductsToOptions();
    }

    ensureOptionImage(option) {
      return option.querySelector('img');
    }

    // src nur anfassen, wenn sich die Quelle wirklich aendert. Jede Zuweisung
    // startet einen neuen Ladevorgang, und fuer die Dauer davon ist der Slot
    // leer - auch wenn das Bild laengst im Cache liegt.
    setOptionImage(image, src, alt) {
      if (!image || !src) return;

      if (image.getAttribute('src') !== src) {
        image.src = src;
        image.removeAttribute('srcset');
      }

      if (alt != null) image.alt = alt;
    }

    resetOptionElement(option, fallbackImage) {
      option.removeAttribute('data-checked');
      option.removeAttribute('data-border');
      this.setOptionImage(option.querySelector('img'), fallbackImage);
    }

    renderOptionElement(option, product, fallbackImage) {
      if (!product) return;
      const image = this.ensureOptionImage(option);

      if (!image) return;

      if (product.image) {
        this.setOptionImage(image, product.image, product.title || '');
      } else if (fallbackImage) {
        this.setOptionImage(image, fallbackImage);
      }

      option.setAttribute('data-checked', '');
    }

    renderOptionList(options, renderList, fallbackImage) {
      if (!options) return;

      // Frueher wurden erst alle Slots geleert und dann wieder befuellt. Das hat
      // auch die Slots kurz weiss aufblitzen lassen, die belegt bleiben.
      options.forEach((option, index) => {
        const product = renderList[index];

        if (product) {
          this.renderOptionElement(option, product, fallbackImage);
        } else {
          this.resetOptionElement(option, fallbackImage);
        }
      });
    }

    updateOptions(state) {
      if (!this.options || !state) return;
      const renderList = state.optionsRender || state.products || [];

      this.renderOptionList(this.options, renderList, state.notSelectedImage);
      const safeActiveOption = this.calculateSafeActiveOption(state);
      this.updateOptionBorders(safeActiveOption);
      this.updateSelectedBowlsDisplay(state);
    }

    calculateSafeActiveOption(state) {
      const maxByQuantity = Number.isFinite(state?.maxQuantity)
        ? Math.max(0, state.maxQuantity - 1)
        : this.options.length - 1;
      const hardMax = Math.max(0, Math.min(maxByQuantity, this.options.length - 1));
      return Math.min(
        Math.max(0, Number.isFinite(state?.activeOption) ? state.activeOption : 0),
        hardMax
      );
    }

    updateOptionBorders(activeOption) {
      if (!this.options) return;

      const safeActive = this.calculateSafeActiveIndex(activeOption);
      this.clearAllOptionBorders();
      this.setBorderForActiveOption(safeActive);
    }

    calculateSafeActiveIndex(activeOption) {
      return Math.min(
        Math.max(0, Number.isFinite(activeOption) ? activeOption : 0),
        this.options.length - 1
      );
    }

    setBorderForActiveOption(safeActive) {
      if (safeActive >= 0 && safeActive < this.options.length) {
        const isLastOption = safeActive === this.options.length - 1;
        const isChecked = this.options[safeActive].hasAttribute('data-checked');

        // Don't set border if it's the last option and it's checked
        if (isLastOption && isChecked) {
          return;
        }

        this.options[safeActive].setAttribute('data-border', '');
      }
    }

    clearAllOptionBorders() {
      if (!this.options) return;
      this.options.forEach((option) => {
        option.removeAttribute('data-border');
      });
    }

    updateSelectedBowlsDisplay(state) {
      const selectedBowlsElement = this.querySelector('[data-selected-bowls]');
      const selectedTotalElement = this.querySelector('[data-selected-total]');

      if (!selectedBowlsElement || !selectedTotalElement || !state) return;

      // Calculate filled count from state
      const bowlsFilled = (state.bowlProducts || []).filter(Boolean).length;
      const optionalFilled = (state.optionalProducts || []).length;
      const filledCount = bowlsFilled + optionalFilled;
      const bowlsQuantity = state.bowlsQuantity || 0;

      // Show only the count (numerator) in data-selected-bowls
      selectedBowlsElement.textContent = filledCount;

      const totalQuantity = filledCount <= bowlsQuantity ? bowlsQuantity : filledCount;
      const format =
        (selectedTotalElement.dataset.quantityTextFormat || "").trim() ||
        "von %quantity% ausgewählt";
      selectedTotalElement.textContent = format.replaceAll(
        "%quantity%",
        String(totalQuantity)
      );
    }

    formatMoney(price) {
      // Waehrung kommt aus snippets/money-format.liquid, damit in Maerkten mit
      // anderer Waehrung nicht der umgerechnete Betrag mit Eurozeichen dasteht.
      return window.brekkyFormatPrice
        ? window.brekkyFormatPrice(price, true)
        : (price / 100).toFixed(2).replace(".", ",").replace(",00", "");
    }

    getActiveOptionPrice(state) {
      if (state?.useVariants) {
        // Legacy variant mode: price comes from the matched wrapper variant
        const currentVariant = state?.currentVariant;

        if (currentVariant) {
          return {
            price: currentVariant.price || 0,
            compareAtPrice: currentVariant.compareAtPrice || 0
          };
        }

        // Fallback: When currentVariant is not available, get price from first variant (first option element)
        if (this.options && this.options.length > 0) {
          const firstOption = this.options[0];
          const price = firstOption.getAttribute('data-price');
          const compareAtPrice = firstOption.getAttribute('data-compare-at-price');

          return {
            price: price ? parseInt(price) : 0,
            compareAtPrice: compareAtPrice ? parseInt(compareAtPrice) : 0
          };
        }
        return { price: 0, compareAtPrice: 0 };
      }

      // Metaobject mode: sum selected product prices, apply per-slot discount
      const optionsRender = state?.optionsRender || [];
      const bowlsQuantity = state?.bowlsQuantity || 0;
      const optionalDiscount = state?.optionalDiscount || 0;
      const bowlsDiscount = state?.bowlsDiscount || 0;
      const wrapperPrice = state?.wrapperPrice || 0;
      const wrapperCompareAtPrice = state?.wrapperCompareAtPrice || 0;

      const filled = optionsRender.filter(item => item !== null && item !== undefined);
      if (filled.length === 0) {
        return { price: wrapperPrice, compareAtPrice: wrapperCompareAtPrice };
      }

      let price = 0;
      let compareAtPrice = 0;
      optionsRender.forEach((item, index) => {
        if (!item) return;
        const itemPrice = item.price ? parseInt(item.price, 10) : 0;
        const itemCompare = item.compareAtPrice ? parseInt(item.compareAtPrice, 10) : itemPrice;
        const isOptional = index >= bowlsQuantity;
        const discount = isOptional ? optionalDiscount : bowlsDiscount;
        if (discount > 0) {
          price += Math.round(itemPrice * (1 - discount / 100));
        } else {
          price += itemPrice;
        }
        compareAtPrice += itemCompare;
      });

      return { price, compareAtPrice };
    }

    updatePrices(state) {
      if (!this.prices || !this.comparePrices) return;

      const { price, compareAtPrice } = this.getActiveOptionPrice(state);

      // Update regular prices
      this.prices.forEach(priceElement => {
        priceElement.textContent = this.formatMoney(price);
        priceElement.setAttribute('data-final-price', String(price));
      });

      // Check if compare_at_price exists and is greater than price
      const hasComparePrice = compareAtPrice && compareAtPrice > price;

      this.comparePrices.forEach(comparePriceElement => {
        if (hasComparePrice) {
          comparePriceElement.textContent = this.formatMoney(compareAtPrice);
          comparePriceElement.style.display = '';
        } else {
          comparePriceElement.style.display = 'none';
        }
      });

      if (!state?.useVariants) {
        document.querySelectorAll('[data-product-info] .price').forEach(container => {
          container.classList.toggle('price--on-sale', !!hasComparePrice);
        });

        const sparePercent = hasComparePrice
          ? Math.round((compareAtPrice - price) * 100 / compareAtPrice)
          : 0;
        document.querySelectorAll('[data-bundle-spare-badge]').forEach(badge => {
          if (hasComparePrice && sparePercent > 0) {
            const percentEl = badge.querySelector('[data-bundle-spare-percent]');
            if (percentEl) percentEl.textContent = String(sparePercent);
            badge.style.display = '';
          } else {
            badge.style.display = 'none';
          }
        });
      }

      document.dispatchEvent(
        new CustomEvent('new-configurator:price-updated', {
          detail: { price, compareAtPrice }
        })
      );
    }

    setupAddButtonListener() {
      if (!this.addButton || !this.configuratorSidebar) return;

      // If cross-selling exists, let cross-selling handle the click / cart add
      const crossSelling = document.querySelector('cross-selling');
      if (crossSelling) return;

      this.addButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.configuratorSidebar.addToDrawer();
      });
    }

  }
  customElements.define('new-configurator', NewConfigurator);
}

if (!customElements.get('configurator-sidebar')) {
  class ConfiguratorSidebar extends HTMLElement {
    constructor() {
      super();
      this.newConfigurator = document.querySelector('new-configurator');
      this.configuratorSlider = this.querySelector('configurator-slider');
      this.closeButton = this.querySelector('[data-close]');
      this.filters = this.querySelectorAll('[data-filter]');
      this.tabs = this.querySelectorAll('[data-tab]');
      this.aside = this.querySelector('[data-aside]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.products = this.querySelectorAll('[data-product]');
      this.options = this.querySelectorAll('[data-option]');

      this.prices = document.querySelectorAll('[data-product-info] [data-final-price]');
      this.comparePrices = document.querySelectorAll('[data-product-info] [data-compare-price]');

      this.sidebarButton = this.querySelector('[data-sidebar-button]');
      this.cartDrawer = document.querySelector('cart-drawer');

      // optionalDiscount/bowlsDiscount are in percent. useVariants toggles between
      // legacy wrapper-variant pricing and metaobject-driven pricing.
      this.initialState = {
        activeOption: 0,
        maxQuantity: parseInt(this.getAttribute('data-max-quantity')),
        bowlsQuantity: parseInt(this.getAttribute('data-bowls-quantity')),
        optionalQuantity: parseInt(this.getAttribute('data-optional-quantity')),
        optionalDiscount: parseInt(this.getAttribute('data-optional-discount') || '0', 10) || 0,
        bowlsDiscount: parseInt(this.getAttribute('data-bowls-discount') || '0', 10) || 0,
        useVariants: this.hasAttribute('data-use-variants'),
        activeFilter: "all",
        notSelectedImage: this.getAttribute('data-not-selected-image'),
        wrapperVariantId: this.getAttribute('data-wrapper-variant-id') || null,
        wrapperPrice: parseInt(this.getAttribute('data-wrapper-price') || '0', 10),
        wrapperCompareAtPrice: parseInt(this.getAttribute('data-wrapper-compare-at-price') || '0', 10),
        giftProducts: JSON.parse(this.getAttribute('data-gift-products') || '[]'),
        bowlProducts: [],
        optionalProducts: [], // compact list
        products: [],
        optionsRender: [],
        currentVariant: {
          id: null,
          price: 0,
          compareAtPrice: 0,
          title: null
        },
        allVariants: this.querySelector('[data-all-variants]') ? JSON.parse(this.querySelector('[data-all-variants]').textContent) : [],

      };
      this.initialState.bowlProducts = new Array(this.initialState.bowlsQuantity || 0).fill(null); // fixed slots with holes

      this.state = { ...this.initialState };
      this.recomputeDerivedState();
    }

    connectedCallback() {
      this.setupListeners();
      this.updateOptionBorders();

      // Initialize new-configurator options
      if (this.newConfigurator) {
        this.newConfigurator.updateOptions(this.state);
      }

      // Initialize selected bowls display
      this.updateSelectedBowlsDisplay();

      // Initialize product quantities based on current state
      this.initializeProductQuantities();

      // Initialize button states
      this.updateButtonStates();

      // Initialize the displayed price from the configured base variant so the
      // empty state shows e.g. "3 Dose (0)" (31,99 €) instead of the server-rendered
      // default variant. Without this the price is only corrected after the first
      // interaction (renderProductsToOptions -> updatePrices).
      this.updatePrices(this.state);
    }

    initializeProductQuantities() {
      this.products.forEach((product) => {
        const productId = product.dataset.id || product.dataset.sku;
        if (productId) {
          this.updateProductQuantity(product, productId);
        }
      });
    }

    getOptionTypeFromProduct(product) {
      // Explicit data-option wins; otherwise infer by current active slot
      if (product?.dataset?.option) return product.dataset.option;
      return this.state.activeOption >= this.state.bowlsQuantity ? 'optional' : 'bowl';
    }

    isBowlOption(optionIndex) {
      return Number.isInteger(optionIndex) && optionIndex < this.state.bowlsQuantity;
    }

    getFilledCount() {
      const bowlsFilled = this.state.bowlProducts.filter(Boolean).length;
      return bowlsFilled + this.state.optionalProducts.length;
    }

    getNextActiveOptionIndex() {
      const bowlHole = this.state.bowlProducts.findIndex((p) => !p);
      if (bowlHole !== -1) return bowlHole;

      const optionalIndex = this.state.optionalProducts.length;
      const optionalStart = this.state.bowlsQuantity;
      const candidate = optionalStart + optionalIndex;
      return Math.min(candidate, this.options.length - 1);
    }

    buildOptionsRenderList() {
      const renderList = new Array(this.options.length).fill(null);
      const useVariants = this.state.useVariants;

      // Bowls keep their holes
      this.state.bowlProducts.forEach((product, idx) => {
        if (idx < renderList.length && product) {
          if (useVariants) {
            // Set variant ID from the option element at this index
            const optionElement = this.options?.[idx];
            const variantId = optionElement?.dataset?.variantId || product.variantId || null;
            renderList[idx] = { ...product, variantId };
          } else {
            renderList[idx] = { ...product };
          }
        }
      });

      // Optional compacts
      const optionalStart = this.state.bowlsQuantity;
      this.state.optionalProducts.forEach((product, idx) => {
        const target = optionalStart + idx;
        if (target < renderList.length && product) {
          if (useVariants) {
            // Set variant ID from the option element at this index
            const optionElement = this.options?.[target];
            const variantId = optionElement?.dataset?.variantId || product.variantId || null;
            renderList[target] = { ...product, variantId };
          } else {
            renderList[target] = { ...product };
          }
        }
      });

      return renderList;
    }

    recomputeDerivedState() {
      this.state.optionsRender = this.buildOptionsRenderList();
      this.state.products = [
        ...this.state.bowlProducts.filter(Boolean),
        ...this.state.optionalProducts,
      ];

      // Legacy variant mode only: resolve the wrapper variant for the selection.
      // In metaobject mode currentVariant stays null and pricing uses _cb_prices.
      if (this.state.useVariants) {
      // Set currentVariant based on allVariants if available
      const optionsRender = this.state.optionsRender || [];
      const allVariants = this.state.allVariants || [];

      if (allVariants.length > 0) {
        // How many bowls are selected, and the configured base bowl count.
        const selectedCount = optionsRender.filter(item => item !== null && item !== undefined).length;
        const baseCount = Number.isFinite(this.state.bowlsQuantity) ? this.state.bowlsQuantity : 4;
        const totalCount = Math.max(baseCount, selectedCount);

        // Count items with additionalPrice: true (strictly boolean true)
        // Only count items where additionalPrice is explicitly true, not truthy values
        const additionalPriceCount = optionsRender.filter(
          item => {
            if (item === null || item === undefined) return false;
            // Explicitly check for boolean true, not truthy values
            return item.additionalPrice === true;
          }
        ).length;

        // Variants are named like "3 Dose (0)" or "4 Dosen (0)": the leading number is the
        // total bowls, the trailing "(N)" is how many are the pricier choice. Match by the
        // trailing "(N)" — this is the source of truth and avoids depending on a correctly
        // configured bowls_quantity (which defaults to 4 and would otherwise break a 3-bowl set).
        const parseTitle = (variant) => {
          if (typeof variant.title !== 'string') return null;
          // total = leading number ("6 cans", "6 Dosen (2)"). The trailing "(M)" is
          // how many picks are the pricier choice; when a title omits it (e.g. the
          // English "6 cans") treat additional as 0 so count-based matching still works.
          const totalMatch = variant.title.match(/^(\d+)/);
          if (totalMatch === null) return null;
          const additionalMatch = variant.title.match(/\((\d+)\)/);
          return {
            total: parseInt(totalMatch[1], 10),
            additional: additionalMatch === null ? 0 : parseInt(additionalMatch[1], 10),
          };
        };
        const candidates = allVariants.filter(variant => {
          const parsed = parseTitle(variant);
          return parsed !== null && parsed.additional === additionalPriceCount;
        });
        // A single candidate means one total exists — use it directly. Multiple candidates means
        // the product offers different totals (more_quantity_options), so disambiguate by the
        // selected total (e.g. "4 Dosen (1)" vs "5 Dosen (1)").
        const matchedVariant = candidates.length <= 1
          ? candidates[0]
          : (candidates.find(variant => parseTitle(variant).total === totalCount) || candidates[0]);

        if (matchedVariant) {
          this.state.currentVariant = {
            id: matchedVariant.id ? String(matchedVariant.id) : null,
            price: matchedVariant.price ? parseInt(matchedVariant.price) : 0,
            compareAtPrice: matchedVariant.compare_at_price ? parseInt(matchedVariant.compare_at_price) : 0,
            title: matchedVariant.title || null
          };
        } else {
          // Fallback to last product if no variant matched
          let lastProduct = null;
          for (let i = optionsRender.length - 1; i >= 0; i--) {
            if (optionsRender[i] !== null && optionsRender[i] !== undefined) {
              lastProduct = optionsRender[i];
              break;
            }
          }

          if (lastProduct) {
            // Get variant title from allVariants by variantId
            const variantId = lastProduct.variantId ? String(lastProduct.variantId) : null;
            const variant = variantId ? allVariants.find(v => String(v.id) === variantId) : null;
            const variantTitle = variant?.title || null;

            this.state.currentVariant = {
              id: variantId,
              price: lastProduct.price ? parseInt(lastProduct.price) : 0,
              compareAtPrice: lastProduct.compareAtPrice ? parseInt(lastProduct.compareAtPrice) : 0,
              title: variantTitle
            };
          } else {
            // When optionsRender is empty, get variant info from first option element
            if (this.options && this.options.length > 0) {
              const firstOption = this.options[0];
              const variantId = firstOption.dataset.variantId || null;
              // Get variant title from allVariants by variantId
              const variant = variantId ? allVariants.find(v => String(v.id) === variantId) : null;
              const variantTitle = variant?.title || null;

              this.state.currentVariant = {
                id: variantId,
                price: firstOption.getAttribute('data-price') ? parseInt(firstOption.getAttribute('data-price')) : 0,
                compareAtPrice: firstOption.getAttribute('data-compare-at-price') ? parseInt(firstOption.getAttribute('data-compare-at-price')) : 0,
                title: variantTitle
              };
            } else {
              this.state.currentVariant = {
                id: null,
                price: 0,
                compareAtPrice: 0,
                title: null
              };
            }
          }
        }
      } else {
        // Original logic when allVariants is empty
        // When allVariants is empty, we can't get variant title, so set to null
        let lastProduct = null;
        for (let i = optionsRender.length - 1; i >= 0; i--) {
          if (optionsRender[i] !== null && optionsRender[i] !== undefined) {
            lastProduct = optionsRender[i];
            break;
          }
        }

        if (lastProduct) {
          this.state.currentVariant = {
            id: lastProduct.variantId || null,
            price: lastProduct.price ? parseInt(lastProduct.price) : 0,
            compareAtPrice: lastProduct.compareAtPrice ? parseInt(lastProduct.compareAtPrice) : 0,
            title: null // Can't get variant title when allVariants is empty
          };
        } else {
          // When optionsRender is empty, get variant info from first option element
          if (this.options && this.options.length > 0) {
            const firstOption = this.options[0];
            this.state.currentVariant = {
              id: firstOption.dataset.variantId || null,
              price: firstOption.getAttribute('data-price') ? parseInt(firstOption.getAttribute('data-price')) : 0,
              compareAtPrice: firstOption.getAttribute('data-compare-at-price') ? parseInt(firstOption.getAttribute('data-compare-at-price')) : 0,
              title: null // Can't get variant title when allVariants is empty
            };
          } else {
            this.state.currentVariant = {
              id: null,
              price: 0,
              compareAtPrice: 0,
              title: null
            };
          }
        }
      }
      }

      const nextIndex = this.getNextActiveOptionIndex();
      this.state.activeOption = this.clampActiveOption(nextIndex);
      this.updateButtonStates();
    }

    clampActiveOption(value = 0) {
      const optionsLength = this.options?.length || 0;
      const maxByQuantity = Number.isFinite(this.state?.maxQuantity)
        ? Math.max(0, this.state.maxQuantity - 1)
        : optionsLength - 1;
      const hardMax = Math.max(0, Math.min(maxByQuantity, optionsLength - 1));
      const safeValue = Number.isFinite(value) ? value : 0;
      return Math.min(Math.max(0, safeValue), hardMax);
    }

    setupListeners() {
      this.setupCloseButtonListener();
      this.setupAsideClickListener();
      this.setupBackdropClickListener();
      this.setupOutsideClickListener();
      this.setupFilterListeners();
      this.setupProductListeners();
      this.setupOptionRemovalListeners();
      this.setupSidebarButtonListener();
    }

    setupCloseButtonListener() {
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => {
          this.closeSidebar();
        });
      }

      // Escape closes the panel, like any other dialog.
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('data-open')) {
          event.stopPropagation();
          this.closeSidebar();
        }
      });
    }

    setupAsideClickListener() {
      if (this.aside) {
        this.aside.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      }
    }

    setupBackdropClickListener() {
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => {
          if (this.hasAttribute('data-open')) {
            this.closeSidebar();
          }
        });
      }
    }

    setupOutsideClickListener() {
      document.addEventListener('click', this.handleOutsideClick.bind(this));
    }

    setupFilterListeners() {
      this.filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          const filterValue = filter.getAttribute('data-filter');
          this.setActiveFilter(filter);
          this.updateTabs(filterValue);
        });
      });
    }

    setupProductListeners() {
      this.products.forEach((product) => {
        product.addEventListener('click', (e) => {
          const minusButton = e.target.closest('[data-minus]');
          if (minusButton) {
            e.stopPropagation();
            this.handleMinusClick(product);
            this.configuratorSlider?.reinitialize();
            return;
          }
          this.handlePlusClick(product);
          this.configuratorSlider?.reinitialize();
          console.log(this.state);
        });

        // The card is a role="button", so Enter/Space have to be wired up by hand.
        // Keys landing on the nested +/- buttons are left alone — those fire their
        // own native click.
        product.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if (e.target !== product) return;
          e.preventDefault();
          this.handlePlusClick(product);
          this.configuratorSlider?.reinitialize();
        });
      });
    }

    setupOptionRemovalListeners() {
      this.options.forEach((option) => {
        option.addEventListener('click', (event) => this.handleOptionClick(option, event));
      });
    }

    handleOptionClick(option, event) {
      event.stopPropagation();
      if (!this.isOptionChecked(option)) return;

      this.clearOptionVisualState(option);

      const optionIndex = Array.from(this.options).indexOf(option);
      const product = this.state?.optionsRender?.[optionIndex];
      if (product) {
        this.removeOptionProductFromState(optionIndex);
      }

      this.renderProductsToOptions();
      this.configuratorSlider?.reinitialize();
      console.log(this.state);
    }

    removeOptionProductFromState(optionIndex) {
      const product = this.state?.optionsRender?.[optionIndex];
      this.removeProductFromState(optionIndex);
      if (product) {
        this.syncProductCardAfterRemoval(product.id);
      }
    }

    syncProductCardAfterRemoval(productId) {
      const productElement = Array.from(this.products).find((p) => p.dataset.id === productId);
      if (!productElement) return;

      this.updateProductQuantity(productElement, productId);
      if (this.hasProductInState(productId)) return;

      this.resetProductCardVisualState(productElement);
    }

    resetProductCardVisualState(productElement) {
      productElement.removeAttribute('data-checked');
      // Die Karte im Raster zeigt immer ihr eigenes Produktbild. Das
      // Platzhalterbild gehoert nur in die leeren Slots. Stand es hier, blieb
      // die Karte nach dem Abwaehlen dauerhaft auf dem leeren Standardbild.
      this.updateProductImageToProduct(productElement);
    }

    clearOptionVisualState(option) {
      option.removeAttribute('data-checked');
      this.setOptionImage(option.querySelector('img'), this.state.notSelectedImage);
    }

    isOptionChecked(option) {
      return option.hasAttribute('data-checked');
    }

    handleMinusClick(product) {
      const productId = product.dataset.id;

      // Remove one instance of the product from state
      this.removeOneProductInstance(productId);

      // Update quantity display
      this.updateProductQuantity(product, productId);

      // If no more instances, reset visual state
      if (!this.hasProductInState(productId)) {
        this.resetProductCardVisualState(product);
      } else {
        // Product still in state, ensure it's marked as checked and has correct image
        product.setAttribute('data-checked', '');
        this.updateProductImageToProduct(product);
      }

      this.renderProductsToOptions();
    }

    updateProductImageToProduct(productElement) {
      this.setOptionImage(productElement.querySelector('.new-configurator__image'), productElement.dataset.image);
    }

    removeOneProductInstance(productId) {
      // Try to remove from optional products first (last added)
      for (let i = this.state.optionalProducts.length - 1; i >= 0; i--) {
        if (this.state.optionalProducts[i]?.id === productId) {
          this.state.optionalProducts.splice(i, 1);
          this.recomputeDerivedState();
          return;
        }
      }

      // Otherwise remove from bowl products (last occurrence)
      for (let i = this.state.bowlProducts.length - 1; i >= 0; i--) {
        if (this.state.bowlProducts[i]?.id === productId) {
          this.state.bowlProducts[i] = null;
          // Compact to fill the gap
          this.compactBowlProducts();
          this.recomputeDerivedState();
          return;
        }
      }
    }

    handlePlusClick(product) {
      if (this.getFilledCount() >= this.state.maxQuantity) {
        return;
      }

      product.setAttribute('data-checked', '');
      this.updateProductImageToProduct(product);
      this.addProductToState(product);
      this.updateProductQuantity(product, product.dataset.id);
      this.renderProductsToOptions();
    }

    removeProductFromState(optionIndex) {
      // Rebuild the whole selection from the current render list minus the clicked
      // slot. Using one linear list (instead of separate bowl-index / optional-index
      // math + compaction) removes the desync that made middle removals fail: the
      // slot cleared visually while the item stayed in the array, so the price kept
      // the old (higher) count.
      const render = this.state.optionsRender || [];
      const clicked = render[optionIndex];
      if (!clicked) {
        this.recomputeDerivedState();
        return;
      }

      // Position of the clicked item among the filled (non-null) items.
      let linearPos = 0;
      for (let i = 0; i < optionIndex; i++) {
        if (render[i] !== null && render[i] !== undefined) linearPos += 1;
      }

      const linear = render.filter((item) => item !== null && item !== undefined);
      linear.splice(linearPos, 1);

      // Repopulate the fixed bowl slots first, then the optional list.
      const bowlsQuantity = this.state.bowlsQuantity || 0;
      const totalBowlSlots = this.state.bowlProducts.length || bowlsQuantity;
      const newBowls = new Array(totalBowlSlots).fill(null);
      for (let i = 0; i < bowlsQuantity && i < linear.length; i++) {
        newBowls[i] = linear[i];
      }
      this.state.bowlProducts = newBowls;
      this.state.optionalProducts = linear.slice(bowlsQuantity);

      this.recomputeDerivedState();
    }

    compactBowlProducts() {
      // Keep compacting until no more gaps can be filled
      let changed = true;
      while (changed) {
        changed = false;

        // Compact bowl products by shifting non-null values forward
        const compactedBowls = [];
        for (let i = 0; i < this.state.bowlProducts.length; i++) {
          if (this.state.bowlProducts[i] !== null) {
            compactedBowls.push(this.state.bowlProducts[i]);
          }
        }

        // Fill remaining slots with nulls
        while (compactedBowls.length < this.state.bowlProducts.length) {
          compactedBowls.push(null);
        }

        // If there's still a gap and we have optional products, move the first optional to fill it
        const firstNullIndex = compactedBowls.findIndex(p => p === null);
        if (firstNullIndex !== -1 && this.state.optionalProducts.length > 0) {
          const firstOptional = this.state.optionalProducts.shift();
          compactedBowls[firstNullIndex] = firstOptional;
          this.state.bowlProducts = compactedBowls;
          changed = true; // Continue loop to check for more gaps
        } else {
          this.state.bowlProducts = compactedBowls;
          break; // No more changes possible
        }
      }
    }

    removeBowlProduct(optionIndex) {
      if (Number.isInteger(optionIndex) && optionIndex < this.state.bowlProducts.length) {
        // Remove the bowl product at the given index
        this.state.bowlProducts[optionIndex] = null;
        // Compact to fill the gap
        this.compactBowlProducts();
      }
    }

    removeOptionalProduct(optionIndex) {
      const optionalIdx = this.convertToOptionalIndex(optionIndex);

      if (optionalIdx !== undefined && optionalIdx < this.state.optionalProducts.length) {
        this.state.optionalProducts.splice(optionalIdx, 1);
      }
    }

    convertToOptionalIndex(optionIndex) {
      if (Number.isInteger(optionIndex) && optionIndex >= this.state.bowlsQuantity) {
        return optionIndex - this.state.bowlsQuantity;
      }
      return undefined;
    }

    addProductToState(product) {
      const optionType = this.getOptionTypeFromProduct(product);
      const productData = this.extractProductData(product, optionType);

      if (this.isBowlOption(this.state.activeOption)) {
        this.addBowlProduct(productData);
      } else {
        this.addOptionalProduct(productData);
      }

      this.recomputeDerivedState();
    }

    extractProductData(product, optionType) {
      // Ensure additionalPrice is always a boolean
      const additionalPrice = product.hasAttribute('data-additional-price') === true;

      let variantId = null;
      let price;
      let compareAtPrice;
      if (this.state.useVariants) {
        // Legacy mode: variant/price come from the active wrapper option slot
        const activeOptionElement = this.options?.[this.state.activeOption];
        variantId = activeOptionElement?.dataset?.variantId || null;
        price = activeOptionElement?.getAttribute('data-price') || null;
        compareAtPrice = activeOptionElement?.getAttribute('data-compare-at-price') || null;
      } else {
        // Metaobject mode: price comes from the selected product itself
        price = product.getAttribute('data-price') || null;
        compareAtPrice = product.getAttribute('data-compare-at-price') || null;
      }

      return {
        id: product.dataset.id || product.dataset.sku, // Use data-id if available, fallback to sku
        handle: product.dataset.handle || '',
        title: product.dataset.title,
        image: product.dataset.image,
        sku: product.dataset.sku,
        type: optionType,
        variantId: variantId,
        price: price,
        compareAtPrice: compareAtPrice,
        additionalPrice: additionalPrice, // Always boolean: true or false
      };
    }

    addBowlProduct(productData) {
      const slotIndex = this.state.bowlProducts.findIndex((p) => p === null);
      if (slotIndex === -1) return;
      this.state.bowlProducts[slotIndex] = productData;
    }

    addOptionalProduct(productData) {
      if (this.state.optionalProducts.length >= this.state.optionalQuantity) return;
      this.state.optionalProducts.push(productData);
    }

    getProductCount(productId) {
      const bowls = this.state.bowlProducts.filter(p => p && p.id === productId).length;
      const optionals = this.state.optionalProducts.filter(p => p.id === productId).length;
      return bowls + optionals;
    }

    hasProductInState(productId) {
      return this.getProductCount(productId) > 0;
    }

    updateProductQuantity(product, productId) {
      const count = this.getProductCount(productId);
      const quantityInput = product.querySelector('[data-quantity]');
      if (quantityInput) {
        quantityInput.value = Math.max(0, count);
      }
      if (count <= 0) {
        product.removeAttribute('data-checked');
      }
    }

    updateOptionBorders() {
      if (!this.options) return;

      this.state.activeOption = this.clampActiveOption(this.state.activeOption);
      this.clearAllOptionBorders();
      this.setActiveOptionBorder();
    }

    clearAllOptionBorders() {
      this.options.forEach((option) => {
        option.removeAttribute('data-border');
      });
    }

    setActiveOptionBorder() {
      if (this.state.activeOption >= 0 && this.state.activeOption < this.options.length) {
        const isLastOption = this.state.activeOption === this.options.length - 1;
        const isChecked = this.options[this.state.activeOption].hasAttribute('data-checked');

        // Don't set border if it's the last option and it's checked
        if (isLastOption && isChecked) {
          return;
        }

        this.options[this.state.activeOption].setAttribute('data-border', '');
      }
    }

    ensureOptionImage(option) {
      return option.querySelector('img');
    }

    // src nur anfassen, wenn sich die Quelle wirklich aendert. Jede Zuweisung
    // startet einen neuen Ladevorgang, und fuer die Dauer davon ist der Slot
    // leer - auch wenn das Bild laengst im Cache liegt.
    setOptionImage(image, src, alt) {
      if (!image || !src) return;

      if (image.getAttribute('src') !== src) {
        image.src = src;
        image.removeAttribute('srcset');
      }

      if (alt != null) image.alt = alt;
    }

    resetOptionElement(option, fallbackImage) {
      option.removeAttribute('data-checked');
      option.removeAttribute('data-border');
      this.setOptionImage(option.querySelector('img'), fallbackImage);
    }

    renderOptionElement(option, product, fallbackImage) {
      if (!product) return;
      const image = this.ensureOptionImage(option);

      if (!image) return;

      if (product.image) {
        this.setOptionImage(image, product.image, product.title || '');
      } else if (fallbackImage) {
        this.setOptionImage(image, fallbackImage);
      }

      option.setAttribute('data-checked', '');
    }

    renderOptionList(options, renderList, fallbackImage) {
      if (!options) return;

      // Frueher wurden erst alle Slots geleert und dann wieder befuellt. Das hat
      // auch die Slots kurz weiss aufblitzen lassen, die belegt bleiben.
      options.forEach((option, index) => {
        const product = renderList[index];

        if (product) {
          this.renderOptionElement(option, product, fallbackImage);
        } else {
          this.resetOptionElement(option, fallbackImage);
        }
      });
    }

    renderProductsToOptions() {
      if (!this.options) return;

      const renderList = this.state.optionsRender || [];
      this.renderOptionList(this.options, renderList, this.state.notSelectedImage);

      // Update option borders after rendering
      this.updateOptionBorders();

      // Update new-configurator options as well
      if (this.newConfigurator) {
        this.newConfigurator.updateOptions(this.state);
      }

      // Update selected bowls display
      this.updateSelectedBowlsDisplay();

      // Update prices
      this.updatePrices(this.state);

      // Update button states
      this.updateButtonStates();

      // Reinitialize slider after render
      this.configuratorSlider?.reinitialize();
    }

    setActiveFilter(activeFilter) {
      this.filters.forEach((filter) => {
        filter.removeAttribute('data-active');
        filter.removeAttribute('aria-current');
      });
      activeFilter.setAttribute('data-active', '');
      activeFilter.setAttribute('aria-current', 'true');
      const filterValue = activeFilter.getAttribute('data-filter');
      this.state.activeFilter = filterValue;
    }

    updateTabs(filterValue) {
      let activeTab = null;

      this.tabs.forEach((tab) => {
        const tabValue = tab.getAttribute('data-tab');
        tab.removeAttribute('data-open');

        if (tabValue === filterValue) {
          tab.setAttribute('data-open', '');
          activeTab = tab;
        }
      });

      this.moveTabToTop(activeTab);
    }

    // Reorder in the DOM rather than with CSS order, so the category that is
    // shown first is also the one tab reaches first. The remaining categories
    // keep their original order behind it.
    moveTabToTop(activeTab) {
      if (!activeTab) return;

      const container = activeTab.parentElement;
      if (!container) return;

      if (!this.originalTabOrder) {
        this.originalTabOrder = Array.from(this.tabs);
      }

      const rest = this.originalTabOrder.filter((tab) => tab !== activeTab && container.contains(tab));
      container.append(activeTab, ...rest);
    }

    handleOutsideClick(event) {
      if (!this.hasAttribute('data-open')) return;
      // Only close if click is completely outside the sidebar element
      // Backdrop clicks are handled by setupBackdropClickListener
      if (!this.contains(event.target)) {
        this.closeSidebar();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutsideClick);
    }

    openSidebar() {
      this.style.zIndex = '1999';
      this.setAttribute('data-open', '');

      // Calculate scrollbar width to prevent page shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Hide overflow and add padding to compensate for scrollbar
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      // Without this the panel opens but keyboard focus stays behind on the
      // slot, so it looks like nothing happened. Remember where we came from
      // so closing can put the focus back.
      const trigger = document.activeElement;
      this.returnFocusTo = trigger && trigger !== document.body ? trigger : null;
      this.focusPanelWhenVisible();
    }

    // The panel is visibility:hidden until the open animation kicks in, and a
    // hidden element cannot take focus. Retry briefly until it is visible.
    focusPanelWhenVisible(attempt = 0) {
      if (!this.hasAttribute('data-open') || attempt > 12) return;

      if (getComputedStyle(this).visibility === 'hidden') {
        setTimeout(() => this.focusPanelWhenVisible(attempt + 1), 40);
        return;
      }

      const target = this.closeButton || this.querySelector('[data-aside] button, [data-aside] [tabindex="0"]');
      target?.focus();
    }

    closeSidebar() {
      this.removeAttribute('data-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      if (this.returnFocusTo && document.contains(this.returnFocusTo)) {
        this.returnFocusTo.focus();
      }
      this.returnFocusTo = null;

      // Keep z-index high during closing animation, then reset after animation completes
      setTimeout(() => {
        if (!this.hasAttribute('data-open')) {
          this.style.zIndex = '-1999';
        }
      }, 300);
    }

    updateSelectedBowlsDisplay() {
      const selectedBowlsElement = document.querySelector('[data-selected-bowls]');
      const selectedTotalElement = document.querySelector('[data-selected-total]');

      if (!selectedBowlsElement || !selectedTotalElement) return;

      const filledCount = this.getFilledCount();
      const bowlsQuantity = this.state.bowlsQuantity;

      // Show only the count (numerator) in data-selected-bowls
      selectedBowlsElement.textContent = filledCount;

      const totalQuantity = filledCount <= bowlsQuantity ? bowlsQuantity : filledCount;
      const format =
        (selectedTotalElement.dataset.quantityTextFormat || "").trim() ||
        "von %quantity% ausgewählt";
      selectedTotalElement.textContent = format.replaceAll(
        "%quantity%",
        String(totalQuantity)
      );
    }


    formatMoney(price) {
      // Waehrung kommt aus snippets/money-format.liquid, damit in Maerkten mit
      // anderer Waehrung nicht der umgerechnete Betrag mit Eurozeichen dasteht.
      return window.brekkyFormatPrice
        ? window.brekkyFormatPrice(price, true)
        : (price / 100).toFixed(2).replace(".", ",").replace(",00", "");
    }

    getActiveOptionPrice(state) {
      if (state?.useVariants) {
        // Legacy variant mode: price comes from the matched wrapper variant
        const currentVariant = state?.currentVariant;

        if (currentVariant) {
          return {
            price: currentVariant.price || 0,
            compareAtPrice: currentVariant.compareAtPrice || 0
          };
        }

        // Fallback: When currentVariant is not available, get price from first variant (first option element)
        if (this.options && this.options.length > 0) {
          const firstOption = this.options[0];
          const price = firstOption.getAttribute('data-price');
          const compareAtPrice = firstOption.getAttribute('data-compare-at-price');

          return {
            price: price ? parseInt(price) : 0,
            compareAtPrice: compareAtPrice ? parseInt(compareAtPrice) : 0
          };
        }
        return { price: 0, compareAtPrice: 0 };
      }

      // Metaobject mode: sum selected product prices, apply per-slot discount
      const optionsRender = state?.optionsRender || [];
      const bowlsQuantity = state?.bowlsQuantity || 0;
      const optionalDiscount = state?.optionalDiscount || 0;
      const bowlsDiscount = state?.bowlsDiscount || 0;
      const wrapperPrice = state?.wrapperPrice || 0;
      const wrapperCompareAtPrice = state?.wrapperCompareAtPrice || 0;

      const filled = optionsRender.filter(item => item !== null && item !== undefined);
      if (filled.length === 0) {
        return { price: wrapperPrice, compareAtPrice: wrapperCompareAtPrice };
      }

      let price = 0;
      let compareAtPrice = 0;
      optionsRender.forEach((item, index) => {
        if (!item) return;
        const itemPrice = item.price ? parseInt(item.price, 10) : 0;
        const itemCompare = item.compareAtPrice ? parseInt(item.compareAtPrice, 10) : itemPrice;
        const isOptional = index >= bowlsQuantity;
        const discount = isOptional ? optionalDiscount : bowlsDiscount;
        if (discount > 0) {
          price += Math.round(itemPrice * (1 - discount / 100));
        } else {
          price += itemPrice;
        }
        compareAtPrice += itemCompare;
      });

      return { price, compareAtPrice };
    }

    updatePrices(state) {
      if (!this.prices || !this.comparePrices) return;

      const { price, compareAtPrice } = this.getActiveOptionPrice(state);

      // Update regular prices
      this.prices.forEach(priceElement => {
        priceElement.textContent = this.formatMoney(price);
        priceElement.setAttribute('data-final-price', String(price));
      });

      // Check if compare_at_price exists and is greater than price
      const hasComparePrice = compareAtPrice && compareAtPrice > price;

      this.comparePrices.forEach(comparePriceElement => {
        if (hasComparePrice) {
          comparePriceElement.textContent = this.formatMoney(compareAtPrice);
          comparePriceElement.style.display = '';
        } else {
          comparePriceElement.style.display = 'none';
        }
      });

      if (!state?.useVariants) {
        document.querySelectorAll('[data-product-info] .price').forEach(container => {
          container.classList.toggle('price--on-sale', !!hasComparePrice);
        });

        const sparePercent = hasComparePrice
          ? Math.round((compareAtPrice - price) * 100 / compareAtPrice)
          : 0;
        document.querySelectorAll('[data-bundle-spare-badge]').forEach(badge => {
          if (hasComparePrice && sparePercent > 0) {
            const percentEl = badge.querySelector('[data-bundle-spare-percent]');
            if (percentEl) percentEl.textContent = String(sparePercent);
            badge.style.display = '';
          } else {
            badge.style.display = 'none';
          }
        });
      }

      document.dispatchEvent(
        new CustomEvent('new-configurator:price-updated', {
          detail: { price, compareAtPrice }
        })
      );
    }

    setupSidebarButtonListener() {
      if (this.sidebarButton) {
        this.sidebarButton.addEventListener('click', () => {
          this.closeSidebar();
        });
      }
    }

    async render() {
      const newHTML = await this.fetchSection();
      const parser = new DOMParser();
      let resAsHtml = parser.parseFromString(newHTML, 'text/html');
      const updatedCart = resAsHtml.querySelector('.cart-drawer');
      const currentContent = document.querySelector('.drawer__inner');
      const newContent = updatedCart.querySelector('.drawer__inner');
      document.querySelector('cart-drawer').classList.remove('is-empty');
      if (currentContent.innerHTML === newContent.innerHTML) return;
      currentContent.innerHTML = newContent.innerHTML;
    }

    async fetchSection() {
      const res = await fetch(
        window.Shopify.routes.root + '?section_id=cart-drawer'
      );
      const text = await res.text();
      return text;
    }

    async addToDrawer() {
      const mainItem = this.getMainItemForCart();
      if (!mainItem) {
        console.error('No variant selected');
        return;
      }

      const formData = {
        items: [mainItem]
      };

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        const data = await response.json();
        await this.render();
        await this.cartDrawer.open();
        this.closeSidebar();
        console.log(data);
      } catch (error) {
        console.error('Error:', error);
      }
    }

    getMainItemForCart() {
      const useVariants = this.state?.useVariants;
      const currentVariant = this.state?.currentVariant;
      const wrapperVariantId = this.state?.wrapperVariantId;
      const optionsRender = this.state?.optionsRender || [];

      const lineItemId = useVariants ? currentVariant?.id : wrapperVariantId;
      if (!lineItemId) {
        return null;
      }

      // Collect all titles and skus from non-null items. The cart drawer renders
      // the custom-set selections from `_Varianten` as plain text titles (the
      // thumbnail-chip strip is reserved for the Mega Bundle).
      const titles = [];
      const skues = [];
      optionsRender.forEach(item => {
        if (item !== null && item !== undefined) {
          if (item.title) titles.push(item.title);
          if (item.sku) skues.push(item.sku);
        }
      });

      const bowlTypeSku = (this.newConfigurator?.getSelectedBowlSku?.() || '').trim();
      if (bowlTypeSku) skues.push(bowlTypeSku);

      // Gift products are added first so they show up at the top of the bundle.
      const giftProducts = this.state?.giftProducts || [];
      if (giftProducts.length > 0) {
        const giftTitles = [];
        giftProducts.forEach(g => {
          if (g.title) giftTitles.push(g.title);
        });
        const giftSkus = giftProducts.map(g => g.sku).filter(Boolean);
        titles.unshift(...giftTitles);
        skues.unshift(...giftSkus);
      }

      const properties = {
        // Underscore-prefixed so Shopify hides it at checkout (the bundle's
        // components are already listed there via the Cart Transform). Holds
        // product TITLES (one per unit); the cart drawer renders them as a plain
        // text list for custom sets.
        "_Varianten": titles.join('; '),
        "Bowl Type": (this.newConfigurator?.getSelectedBowlType?.() || '').trim(),
        "_Artikelnummern": skues.join(',')
      };

      const variantGids = optionsRender
        .filter(item => item?.id)
        .map(item => `gid://shopify/ProductVariant/${item.id}`);
      if (variantGids.length > 0) {
        let selections = variantGids;
        let prices;
        if (useVariants) {
          // The bundle is sold at the selected variant price (e.g. "3 Dosen" =
          // 49,99 €), NOT the sum of the individual can prices. The Cart Transform
          // charges the sum of _cb_prices at checkout, so we must scale the
          // per-component prices to add up to the variant price — otherwise the
          // order total is the raw can sum (e.g. 53,97 € instead of 49,99 €).
          const rawPrices = optionsRender
            .filter(item => item?.id)
            .map(item => {
              const el = document.querySelector(`[data-product][data-id="${item.id}"]`);
              return el ? parseInt(el.getAttribute('data-price') || '0', 10) : 0;
            });
          const variantPrice = parseInt(currentVariant?.price || '0', 10);
          const rawTotal = rawPrices.reduce((sum, p) => sum + p, 0);
          if (variantPrice > 0 && rawTotal > 0) {
            const scaled = rawPrices.map(p => Math.round((p / rawTotal) * variantPrice));
            // Push any rounding drift onto the last component so the sum is exact.
            const drift = variantPrice - scaled.reduce((sum, p) => sum + p, 0);
            scaled[scaled.length - 1] += drift;
            prices = scaled.map(String);
          } else {
            prices = rawPrices.map(String);
          }
          // The variant carries its saving as the matched variant's compare-at price
          // (the same value the product page strikes through). Emit it so the Cart
          // Transform + bundle-discount functions render the struck price at checkout,
          // exactly like metaobject mode does below. Price paid is unchanged: _cb_prices
          // still sums to variantPrice.
          const variantCompareAt = parseInt(currentVariant?.compareAtPrice || '0', 10);
          if (variantCompareAt > variantPrice) {
            properties._cb_compare_total = String(variantCompareAt);
          }
        } else {
          const optionalDiscount = this.state.optionalDiscount || 0;
          const bowlsDiscount = this.state.bowlsDiscount || 0;
          const bowlsQuantity = this.state.bowlsQuantity || 0;
          const filteredItems = optionsRender.filter(item => item?.id);
          prices = filteredItems.map((item, index) => {
            const price = parseInt(item.price || '0', 10);
            const discount = index >= bowlsQuantity ? optionalDiscount : bowlsDiscount;
            if (discount > 0) {
              return Math.round(price * (1 - discount / 100));
            }
            return price;
          });

          const finalTotal = prices.reduce((a, b) => a + b, 0);
          const compareTotal = filteredItems.reduce((sum, item) => {
            const itemPrice = parseInt(item.price || '0', 10);
            const itemCompare = item.compareAtPrice ? parseInt(item.compareAtPrice, 10) : 0;
            return sum + Math.max(itemCompare, itemPrice);
          }, 0);
          if (compareTotal > finalTotal) {
            properties._cb_compare_total = String(compareTotal);
          }
        }

        // Prepend free gifts so they expand as the first components (price 0).
        // _cb_selections and _cb_prices must stay the same length for the Cart
        // Transform function to use real prices instead of splitting evenly.
        if (giftProducts.length > 0) {
          const giftGids = giftProducts.map(g => `gid://shopify/ProductVariant/${g.variantId}`);
          selections = [...giftGids, ...selections];
          prices = [...giftProducts.map(() => 0), ...prices];
        }

        properties._cb_selections = selections.join(',');
        properties._cb_prices = prices.join(',');
      }

      return {
        id: lineItemId,
        quantity: 1,
        properties
      };
    }

    areAllBowlsFilled() {
      return this.state.bowlProducts.every(product => product !== null && product !== undefined);
    }

    disabledButtons() {
      if (this.addButton) {
        this.addButton.setAttribute('aria-disabled', 'true');
        if (this.addButton.parentElement) {
          this.addButton.parentElement.setAttribute('aria-disabled', 'true');
        }
      }
      // Also update the button in NewConfigurator
      if (this.newConfigurator && this.newConfigurator.addButton) {
        this.newConfigurator.addButton.setAttribute('aria-disabled', 'true');
        if (this.newConfigurator.addButton.parentElement) {
          this.newConfigurator.addButton.parentElement.setAttribute('aria-disabled', 'true');
        }
      }
    }

    enabledButtons() {
      if (this.addButton) {
        this.addButton.setAttribute('aria-disabled', 'false');
        if (this.addButton.parentElement) {
          this.addButton.parentElement.setAttribute('aria-disabled', 'false');
        }
      }
      // Also update the button in NewConfigurator
      if (this.newConfigurator && this.newConfigurator.addButton) {
        this.newConfigurator.addButton.setAttribute('aria-disabled', 'false');
        if (this.newConfigurator.addButton.parentElement) {
          this.newConfigurator.addButton.parentElement.setAttribute('aria-disabled', 'false');
        }
      }
    }

    updateButtonStates() {
      if (this.areAllBowlsFilled()) {
        this.enabledButtons();
        if (this.sidebarButton) {
          this.sidebarButton.setAttribute('data-active', '');
        }
      } else {
        this.disabledButtons();
        if (this.sidebarButton) {
          this.sidebarButton.removeAttribute('data-active');
        }
      }
    }
  }
  customElements.define('configurator-sidebar', ConfiguratorSidebar);
}


if (!customElements.get('configurator-slider')) {
  class ConfiguratorSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.left = this.querySelector('[data-left]');
      this.right = this.querySelector('[data-right]');

      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.currentIndex = 0;
      this.isMouseDown = false;
      this.startX = 0;
      this.dragged = false; // Track whether dragging occurred

      this.setupEventListeners();

      this.setupObservers();

      this.visibilityNavigation();

      this.scrollToBorderedSlide();
    }

    setupEventListeners() {
      this.left?.addEventListener('click', () => this.scrollLeft());
      this.right?.addEventListener('click', () => this.scrollRight());

      // Add scroll listener to detect when scrolled to the end
      this.sliderList?.addEventListener('scroll', () => this.handleScroll());

      if (window.innerWidth >= 750) {
        // Mouse drag support
        this.sliderList.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.sliderList.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.sliderList.addEventListener('mouseup', this.onMouseUp.bind(this));
        this.sliderList.addEventListener('mouseleave', this.onMouseUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });

        // Prevent play video on drag
        this.sliderList.querySelectorAll('custom-video').forEach((video) => {
          video.addEventListener('click', (event) => {
            if (this.dragged) {
              event.stopImmediatePropagation();
            }
          });
        });
      }
    }

    handleScroll() {
      if (!this.sliderList) return;

      const scrollLeft = this.sliderList.scrollLeft;
      const scrollWidth = this.sliderList.scrollWidth;
      const clientWidth = this.sliderList.clientWidth;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1; // -1 for rounding errors
      const isAtStart = scrollLeft <= 1; // 1 for rounding errors

      // Ensure proper alignment at start and end
      if (isAtStart) {
        // Snap to start: first slide aligned with list start
        this.sliderList.scrollLeft = 0;
        this.currentIndex = 0;
      } else if (isAtEnd) {
        // Snap to end: last slide aligned with list end
        const maxScroll = scrollWidth - clientWidth;
        this.sliderList.scrollLeft = maxScroll;
        const size = this.sliderList.children.length;
        const slidesPerView = this.getSlidesPerView();
        this.currentIndex = Math.max(0, size - slidesPerView);
      }

      // Update navigation visibility based on scroll position
      this.visibilityNavigation();
    }

    onMouseDown(event) {
      this.isMouseDown = true;
      this.startX = event.pageX - this.sliderList.offsetLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
    }

    onMouseMove(event) {
      if (!this.isMouseDown) return;
      const moveX = event.pageX - this.sliderList.offsetLeft;
      const distance = moveX - this.startX;

      // If movement is significant, set dragged to true
      if (Math.abs(distance) > 5) {
        this.dragged = true;
      }

      if (distance < 0) {
        this.scrollRight();
      } else {
        this.scrollLeft();
      }
    }

    onMouseUp() {
      this.isMouseDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollLeft() {
      if (!this.sliderList || !this.slide) return;
      const newScrollLeft = this.sliderList.scrollLeft - this.slide.clientWidth;
      this.sliderList.scrollLeft = Math.max(0, newScrollLeft);
      // Ensure alignment at start
      if (this.sliderList.scrollLeft <= 1) {
        this.sliderList.scrollLeft = 0;
      }
    }

    scrollRight() {
      if (!this.sliderList || !this.slide) return;
      const scrollWidth = this.sliderList.scrollWidth;
      const clientWidth = this.sliderList.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const newScrollLeft = this.sliderList.scrollLeft + this.slide.clientWidth;
      this.sliderList.scrollLeft = Math.min(maxScroll, newScrollLeft);
      // Ensure alignment at end
      if (this.sliderList.scrollLeft + clientWidth >= scrollWidth - 1) {
        this.sliderList.scrollLeft = maxScroll;
      }
    }

    setupObservers() {
      // Store observer reference for later updates
      if (this.sliderScrollObserver) {
        this.sliderScrollObserver.disconnect();
      }

      // Calculate the rootMargin to only show the current slide based on slidesPerView
      let slidesPerView = this.getSlidesPerView();

      let rootMargin = "0px 0px 0px 0px";
      if (slidesPerView > 1) {
        // Calculate the percentage to hide based on slides per view
        // For 3 slides per view, we want to hide 66.66% from the right
        const hidePercentage = ((slidesPerView - 1) / slidesPerView) * 100;
        rootMargin = `0px -${hidePercentage}% 0px 0px`;
      }
      this.sliderScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              slidesPerView = this.getSlidesPerView();
              this.currentIndex = parseInt(entry.target.dataset.index, 10);
              this.visibilityNavigation();
            }
          });
        },
        {
          root: this.sliderList,
          rootMargin: rootMargin,
          threshold: 0.3
        }
      );
      // Assign `data-index` to each slide for identification
      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        this.sliderScrollObserver.observe(slide);
      });
    }

    visibilityNavigation() {
      if (!this.sliderList) return;

      // Check for overflow first
      this.showSliderNavigationIfOverflowing();

      const scrollLeft = this.sliderList.scrollLeft;
      const scrollWidth = this.sliderList.scrollWidth;
      const clientWidth = this.sliderList.clientWidth;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1; // -1 for rounding errors
      const isAtStart = scrollLeft <= 1; // 1 for rounding errors

      // If all slides are visible at once, hide all buttons
      const totalSlides = this.sliderList.children.length;
      const slidesPerView = this.getSlidesPerView();
      if (totalSlides <= slidesPerView) {
        this.left?.setAttribute('data-active', 'false');
        this.right?.setAttribute('data-active', 'false');
        return;
      }

      // When at start: only right button visible
      if (isAtStart) {
        this.left?.setAttribute('data-active', 'false');
        this.right?.setAttribute('data-active', 'true');
      }
      // When at end: only left button visible
      else if (isAtEnd) {
        this.left?.setAttribute('data-active', 'true');
        this.right?.setAttribute('data-active', 'false');
      }
      // When not at start or end: both buttons visible
      else {
        this.left?.setAttribute('data-active', 'true');
        this.right?.setAttribute('data-active', 'true');
      }
    }

    showSliderNavigationIfOverflowing() {
      if (!this.sliderList) return;

      const hasOverflow = this.sliderList.scrollWidth > this.sliderList.clientWidth;
      if (hasOverflow) {
        this.left?.removeAttribute('data-hide');
        this.right?.removeAttribute('data-hide');
      } else {
        this.left?.setAttribute('data-hide', '');
        this.right?.setAttribute('data-hide', '');
      }
    }

    reinitialize() {
      // Wait for DOM updates before reinitializing
      requestAnimationFrame(() => {
        // Reinitialize observers when content changes
        if (this.sliderScrollObserver) {
          this.sliderScrollObserver.disconnect();
        }

        // Reset current index
        this.currentIndex = 0;
        this.slide = this.sliderList?.children[0] || null;

        // Re-setup observers
        this.setupObservers();

        // Update visibility
        this.visibilityNavigation();

        // Scroll to bordered slide if it exists
        this.scrollToBorderedSlide();
      });
    }

    getSlidesPerView() {
      const sliderContainer = this.sliderList;
      const hasOneButton = this.hasAttribute('data-one-button');

      // When data-one-button is present (configurator), only count checked slides
      let slides = [...this.sliderList.children];
      if (hasOneButton) {
        slides = slides.filter(slide => slide.hasAttribute('data-checked'));
      }

      if (!sliderContainer || slides.length === 0) return 1;

      const containerWidth = sliderContainer.clientWidth;
      const slideWidth = slides[0].clientWidth;

      return Math.max(1, Math.floor(containerWidth / slideWidth));
    }

    scrollToBorderedSlide() {
      if (!this.sliderList || !this.slide) return;

      // Find the slide with data-border attribute
      const borderedSlide = Array.from(this.sliderList.children).find(
        (slide) => slide.hasAttribute('data-border')
      );

      if (borderedSlide) {
        // Calculate the scroll position to center the bordered slide in the middle of the list
        const slideIndex = Array.from(this.sliderList.children).indexOf(borderedSlide);
        const slideWidth = this.slide.clientWidth;
        const containerWidth = this.sliderList.clientWidth;
        const scrollWidth = this.sliderList.scrollWidth;

        // Calculate the position of the bordered slide
        const slidePosition = slideWidth * slideIndex;

        // Calculate scroll position to center the slide: slide center - container center
        const slideCenter = slidePosition + (slideWidth / 2);
        const containerCenter = containerWidth / 2;
        let scrollPosition = slideCenter - containerCenter;

        // Ensure we don't scroll beyond bounds
        const maxScroll = scrollWidth - containerWidth;
        scrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));

        // Scroll to center the bordered slide
        this.sliderList.scrollLeft = scrollPosition;

        // Update current index
        this.currentIndex = slideIndex;

        // Update visibility after scrolling
        requestAnimationFrame(() => {
          this.visibilityNavigation();
        });
      }
    }
  }

  customElements.define('configurator-slider', ConfiguratorSlider);
}
